import React from 'react';
import {
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Button, TextField, FormControl,
  InputLabel, Select, Alert,
} from '@mui/material';
import { MoreVert, FlagOutlined, BlockOutlined } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { apiBlockUser, apiReport, type ReportTargetType } from '../../services/api';

const REPORT_REASONS = [
  'Spam or scam',
  'Harassment or bullying',
  'Inappropriate or explicit content',
  'Hate speech',
  'Impersonation',
  'Other',
];

export interface UserActionsMenuProps {
  /** User to block. Block action is hidden when absent or equal to the current user. */
  targetUserId?: string | null;
  /** Display name used in labels and confirmation copy. */
  targetUserName?: string | null;
  /** What the Report action targets. Report action is hidden when absent. */
  report?: { targetType: ReportTargetType; targetId: string } | null;
  onBlocked?: () => void;
  onReported?: () => void;
  size?: 'small' | 'medium';
  /** Optional color override for the kebab icon (e.g. a feed-token muted color). */
  iconColor?: string;
  iconSx?: SxProps<Theme>;
}

const UserActionsMenu: React.FC<UserActionsMenuProps> = ({
  targetUserId,
  targetUserName,
  report,
  onBlocked,
  onReported,
  size = 'small',
  iconColor,
  iconSx,
}) => {
  const { user: me } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [reportOpen, setReportOpen] = React.useState(false);
  const [blockOpen, setBlockOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [details, setDetails] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const name = targetUserName || 'this user';
  const canBlock = !!targetUserId && targetUserId !== me?.id;
  const canReport = !!report;

  // Nothing actionable — render nothing so callers can drop it in unconditionally.
  if (!canBlock && !canReport) return null;

  const open = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
  const closeMenu = () => setAnchorEl(null);

  const openReport = (e: React.MouseEvent) => {
    e.stopPropagation();
    setReason(''); setDetails(''); setError(null);
    setReportOpen(true);
    setAnchorEl(null);
  };
  const openBlock = (e: React.MouseEvent) => {
    e.stopPropagation();
    setError(null);
    setBlockOpen(true);
    setAnchorEl(null);
  };

  const submitReport = async () => {
    if (!report || !reason || submitting) return;
    setSubmitting(true); setError(null);
    try {
      await apiReport({ targetType: report.targetType, targetId: report.targetId, reason, details: details.trim() || undefined });
      setReportOpen(false);
      onReported?.();
    } catch (err: any) {
      setError(err?.message === 'already_reported' ? "You've already reported this." : 'Could not submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitBlock = async () => {
    if (!targetUserId || submitting) return;
    setSubmitting(true); setError(null);
    try {
      await apiBlockUser(targetUserId);
      setBlockOpen(false);
      onBlocked?.();
    } catch (err: any) {
      setError('Could not block this user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <IconButton
        size={size}
        onClick={handleOpen}
        aria-label="More actions"
        sx={{ color: iconColor ?? 'text.secondary', ...iconSx }}
      >
        <MoreVert fontSize={size} />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu} onClick={(e) => e.stopPropagation()}>
        {canReport && (
          <MenuItem onClick={openReport}>
            <ListItemIcon><FlagOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Report</ListItemText>
          </MenuItem>
        )}
        {canBlock && (
          <MenuItem onClick={openBlock} sx={{ color: 'error.main' }}>
            <ListItemIcon><BlockOutlined fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Block {name}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Report dialog */}
      <Dialog open={reportOpen} onClose={() => !submitting && setReportOpen(false)} fullWidth maxWidth="xs" onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Report content</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Reports are reviewed by moderators. Tell us what's wrong.
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="report-reason-label">Reason</InputLabel>
            <Select
              labelId="report-reason-label"
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {REPORT_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Details (optional)"
            value={details}
            onChange={(e) => setDetails(e.target.value.slice(0, 1000))}
            placeholder="Add any context that will help moderators."
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitReport} disabled={!reason || submitting}>
            {submitting ? 'Submitting…' : 'Submit report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block confirmation */}
      <Dialog open={blockOpen} onClose={() => !submitting && setBlockOpen(false)} fullWidth maxWidth="xs" onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Block {name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You won't see {name}'s posts and neither of you can message the other. You can unblock from Settings later.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitBlock} disabled={submitting}>
            {submitting ? 'Blocking…' : 'Block'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserActionsMenu;
